import React, { useCallback, useMemo } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';

import { makeGetTaskProjectAndSectionLabelList } from '../../../../../../redux/tasks/tasksToProjectsRelation/selectors';
import ProjectPopover from '../../../../../../src-new/components/popovers/ProjectPopover';
import {
  addTaskToProjectAction,
  reorderTasksAction,
} from '../../../../../../redux/tasks/actions';
import { getProjectColourPalette } from '../../../../../../js/helper';

import { isEmpty } from 'lodash';

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '99, 102, 241';
};

const ConnectedTaskProjectsLabelList = ({
  taskId,
  projectAndSectionLabelList,
  myProjects: myProjectsProp,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const allProjects = useSelector((state) => state.projectReducer.myProjects);

  const myProjects = myProjectsProp.map((pp) =>
    allProjects.find((p) => p._id === pp._id),
  );

  const handleProjectNavigate = useCallback((projectId) => navigate(`/${projectId}/board`), [navigate]);

  const handleProjectAdd = async (projectId) => {
    await dispatch(addTaskToProjectAction(taskId, projectId));
  };

  const handleSectionChange = useCallback(
    (currentSectionInfo, newSectionInfo, projectId) => {
      dispatch(
        reorderTasksAction(
          currentSectionInfo._id,
          newSectionInfo._id,
          taskId,
          0,
          0,
          projectId,
        ),
      );
    },
    [taskId],
  );

  const existingProjectIds = new Set(
    Object.values(projectAndSectionLabelList).map((tag) => tag.projectId),
  );

  const projectOptions = myProjects
    ?.filter((proj) => !existingProjectIds.has(proj._id))
    ?.map((proj) => ({ label: proj.title, value: proj._id }));

  const labelsToShow = useMemo(() => {
    let firstLabelToShow;
    const moreLabelsForTooltip = [];

    Object.values(projectAndSectionLabelList)?.forEach((tag, i) => {
      const project = myProjects?.find((proj) => proj?._id === tag?.projectId);
      const sectionObjects = project?.sectionObjects || project?.sections;
      const projectTitle = project?.title;
      const sectionId = tag.sectionId;
      const currentSectionInfo = sectionObjects?.find((so) => so._id === sectionId);
      const sectionTitle = currentSectionInfo?.title;
      const accent = project?.metadata?.colour || project?.color || '#6366f1';
      const rgb = hexToRgb(accent);
      const palette = getProjectColourPalette(accent);
      const textColor = palette.text.startsWith('rgba(') ? palette.text : accent;

      if (i === 0) {
        firstLabelToShow = (
          <div
            key={tag?.projectId}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 max-w-full overflow-hidden"
            style={{
              backgroundColor: palette.bg,
              border: `1px solid ${palette.border}`,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: accent }}
            />
            {projectTitle && (
              <ProjectPopover project={project} color={accent}>
                <span
                  className="text-xs font-medium truncate cursor-pointer"
                  style={{ color: textColor }}
                  onClick={() => handleProjectNavigate(tag?.projectId)}
                >
                  {projectTitle}
                </span>
              </ProjectPopover>
            )}
            {sectionTitle && (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: sectionObjects.map((sec) => ({
                    key: sec._id,
                    label: sec.title,
                    onClick: () => handleSectionChange(currentSectionInfo, sec, tag?.projectId),
                  })),
                }}
                placement="bottomLeft"
              >
                <span
                  className="text-xs truncate cursor-pointer"
                  style={{ color: textColor, opacity: 0.75 }}
                >
                  / {sectionTitle}
                </span>
              </Dropdown>
            )}
          </div>
        );
      } else if (projectTitle) {
        moreLabelsForTooltip.push(`${projectTitle}${sectionTitle ? `/${sectionTitle}` : ''}`);
      }
    });

    return { firstLabelToShow, moreLabelsForTooltip };
  }, [handleProjectNavigate, handleSectionChange, myProjects, projectAndSectionLabelList]);

  return (
    <div className="flex items-center gap-1.5 overflow-hidden w-full">
      <div className="flex items-center gap-1 overflow-hidden flex-1 min-w-0">
        <div className="overflow-hidden flex items-center">
          {labelsToShow?.firstLabelToShow}
        </div>
        {!isEmpty(labelsToShow?.moreLabelsForTooltip) && (
          <Tooltip title={labelsToShow.moreLabelsForTooltip.join(', ')}>
            <span className="text-[11px] text-gray-400 font-medium flex-shrink-0 cursor-default">
              +{labelsToShow.moreLabelsForTooltip.length}
            </span>
          </Tooltip>
        )}
      </div>
      <Dropdown
        trigger={['click']}
        menu={{
          items: projectOptions?.map((project) => ({
            key: project?.value,
            label: project?.label,
            onClick: () => handleProjectAdd(project?.value),
          })),
          style: { maxHeight: 300, overflow: 'scroll' },
        }}
        placement="bottomCenter"
      >
        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 transition-colors cursor-pointer text-gray-400 hover:text-blue-500">
          <AddIcon sx={{ fontSize: 14 }} />
        </span>
      </Dropdown>
    </div>
  );
};

const makeMapStateToProps = () => {
  const getTaskProjectAndSectionLabelList = makeGetTaskProjectAndSectionLabelList();
  return (state, props) => ({
    projectAndSectionLabelList: getTaskProjectAndSectionLabelList(state, props),
    myProjects: state.projectReducer.myProjects,
  });
};

export default connect(makeMapStateToProps)(ConnectedTaskProjectsLabelList);
